import { useMemo } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "./motionUtils";
import { Icon } from "../../../components/Icon";
import { icons } from "../../../components/Icon/iconRegistry";

const Container = styled(motion.div)`
  margin-top: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.main.white};
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const StrengthLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.main.textSecondary};
`;

const StrengthValue = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ProgressContainer = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.colors.border.secondaryLight};
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 12px;
  position: relative;
`;

const ProgressBar = styled(motion.div)`
  height: 100%;
  border-radius: 3px;
  background: ${({ color }) => color};
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const RequirementsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Requirement = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: ${({ met, theme }) =>
    met ? theme.colors.main.successLight : theme.colors.main.white};
  border: 1px solid
    ${({ met, theme }) =>
      met ? theme.colors.main.success : theme.colors.border.secondaryLight};
  transition: all 0.3s ease;
`;

const RequirementIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
`;

const RequirementText = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ met, theme }) =>
    met ? theme.colors.main.success : theme.colors.main.textTertiary};
  transition: all 0.3s ease;
`;

const SecurityTips = styled(motion.div)`
  margin-top: 12px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.main.primaryLight};
  border-radius: 6px;
  border-left: 3px solid ${({ theme }) => theme.colors.main.primary};
`;

const SecurityTipText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.main.primary};
  font-weight: 500;
`;

const calculatePasswordStrength = (password) => {
  if (!password)
    return {
      score: 0,
      label: "Enter password",
      color: "#9ca3af",
      percentage: 0,
      requirements: [],
      tips: [],
    };

  let score = 0;
  const requirements = [];
  const tips = [];

  // Length checks
  const hasMinLength = password.length >= 8;
  const hasGoodLength = password.length >= 12;
  const hasExcellentLength = password.length >= 16;

  if (hasMinLength) score++;
  if (hasGoodLength) score++;
  if (hasExcellentLength) score++;

  requirements.push({
    text: "8+ characters",
    met: hasMinLength,
    icon: hasMinLength ? "greenCheck" : "greyCheck",
  });

  // Character variety
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  // Count character types
  const charTypes = [hasLower, hasUpper, hasNumber, hasSpecial].filter(
    Boolean
  ).length;

  // More generous scoring
  if (charTypes >= 2) score++; // Any 2 types = basic strength
  if (charTypes >= 3) score++; // Any 3 types = good strength
  if (charTypes >= 4) score++; // All 4 types = excellent strength

  // More flexible requirements display
  requirements.push({
    text: "Uppercase letters",
    met: hasUpper,
    icon: hasUpper ? "greenCheck" : "greyCheck",
  });
  requirements.push({
    text: "Lowercase letters",
    met: hasLower,
    icon: hasLower ? "greenCheck" : "greyCheck",
  });
  requirements.push({
    text: "Numbers",
    met: hasNumber,
    icon: hasNumber ? "greenCheck" : "greyCheck",
  });
  requirements.push({
    text: "Special characters",
    met: hasSpecial,
    icon: hasSpecial ? "greenCheck" : "greyCheck",
  });

  // Pattern analysis
  // const hasRepeatingChars = /(.)\1{2,}/.test(password);
  // const hasSequentialChars = /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(password);
  // const hasCommonPatterns = /(password|123|qwerty|admin|letmein)/i.test(password);

  // if (hasRepeatingChars) score = Math.max(0, score - 1);
  // if (hasSequentialChars) score = Math.max(0, score - 1);
  // if (hasCommonPatterns) score = Math.max(0, score - 2);

  // Generate smart tips based on analysis
  if (password.length < 8) {
    tips.push("Use at least 8 characters for basic security");
  } else if (password.length < 12) {
    tips.push("Consider using 12+ characters for stronger security");
  }

  // More encouraging tips based on what's missing
  if (charTypes < 2) {
    tips.push(
      "Add at least 2 different character types (letters, numbers, symbols)"
    );
  } else if (charTypes < 3) {
    tips.push("Add one more character type for better security");
  } else if (charTypes < 4) {
    tips.push("Add special characters for maximum security");
  }

  // if (hasRepeatingChars) {
  //   tips.push('Avoid repeating characters (e.g., "aaa", "111")');
  // }

  // if (hasSequentialChars) {
  //   tips.push('Avoid sequential characters (e.g., "abc", "123")');
  // }

  // if (hasCommonPatterns) {
  //   tips.push('Avoid common words and patterns');
  // }

  // Map score to strength level with more generous thresholds
  const strengthMap = {
    0: { label: "Very Weak", color: "#ef4444", percentage: 15, icon: "⚠️" },
    1: { label: "Weak", color: "#f97316", percentage: 30, icon: "⚠️" },
    2: { label: "Fair", color: "#eab308", percentage: 45, icon: "⚡" },
    3: { label: "Good", color: "#84cc16", percentage: 60, icon: "🔒" },
    4: { label: "Strong", color: "#22c55e", percentage: 80, icon: "🛡️" },
    5: { label: "Very Strong", color: "#10b981", percentage: 100, icon: "🔐" },
    6: { label: "Excellent", color: "#059669", percentage: 100, icon: "🔐" },
  };

  const strength = strengthMap[Math.min(score, 6)];

  return {
    score,
    ...strength,
    requirements,
    tips: tips.slice(0, 2), // Show max 2 tips
  };
};

const PasswordStrengthIndicator = ({
  password,
  show = true,
  showRequirements = false,
  showTips = true,
}) => {
  const strength = useMemo(
    () => calculatePasswordStrength(password),
    [password]
  );

  if (!show || !password) return null;

  return (
    <AnimatePresence>
      <Container
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <Header>
          <StrengthLabel>Password Strength</StrengthLabel>
          <StrengthValue color={strength.color}>
            <span>{strength.icon}</span>
            {strength.label}
          </StrengthValue>
        </Header>

        <ProgressContainer>
          <ProgressBar
            color={strength.color}
            initial={{ width: 0 }}
            animate={{ width: `${strength.percentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </ProgressContainer>

        {showRequirements && (
          <RequirementsGrid>
            {strength.requirements.map((req, index) => (
              <Requirement
                key={index}
                met={req.met}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
              >
                <RequirementIcon met={req.met}>
                  <Icon
                    component={icons[req.icon]}
                    size="14px"
                    color={req.met ? "#42be87" : "#9ca3af"}
                  />
                </RequirementIcon>
                <RequirementText met={req.met}>{req.text}</RequirementText>
              </Requirement>
            ))}
          </RequirementsGrid>
        )}

        {showTips && strength.tips.length > 0 && (
          <SecurityTips
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <SecurityTipText>💡 {strength.tips[0]}</SecurityTipText>
          </SecurityTips>
        )}
      </Container>
    </AnimatePresence>
  );
};

export default PasswordStrengthIndicator;
